import { useEffect, useState } from 'react';
import { Download, Shield, FileJson, Mail } from 'lucide-react';
import {
  Card,
  Button,
  StatusBadge,
  NotificationCard,
  UniversalLoadingState,
  useToast,
} from '@afrilink/ui';
import {
  customerDataExportService,
  type DataExportRequest,
} from '@/domains/popia/services/customerDataExportService';
import { formatDateTime } from '@/shared/utils/format';
import { downloadJson } from '@/shared/utils/jsonExport';

export default function SettingsDataPage() {
  const { toast } = useToast();
  const [requests, setRequests] = useState<DataExportRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  function reload() {
    customerDataExportService
      .listRequests()
      .then(setRequests)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }

  useEffect(reload, []);

  async function runExport() {
    setExporting(true);
    try {
      const payload = await customerDataExportService.export();
      const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      downloadJson(`afrilink-data-export-${stamp}.json`, payload);
      toast({
        tone: 'success',
        title: 'Export downloaded',
        body: 'A complete JSON copy of your data has been saved to your device.',
      });
      reload();
    } catch (e) {
      toast({
        tone: 'danger',
        title: 'Export failed',
        body: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card interactive={false} className="p-4">
        <div className="flex items-start gap-3">
          <Shield className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-navy-primary">Your data</h2>
            <p className="mt-1 text-xs text-foreground-soft">
              Per POPIA §23 you have the right to access every piece of personal information
              SightHost holds about you. Click below to download a complete JSON snapshot:
              profile, hosting accounts, domains, invoices, payments, support history, audit log.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button onClick={runExport} loading={exporting} disabled={exporting}>
                {exporting ? null : <Download className="h-4 w-4" />}
                {exporting ? 'Preparing export…' : 'Download my data (JSON)'}
              </Button>
              <a
                href="mailto:support@sighthost.online?subject=POPIA%20deletion%20request"
                className="inline-flex h-10 items-center gap-2 rounded border border-input bg-card px-4 text-sm font-medium text-foreground hover:bg-muted"
              >
                <Mail className="h-4 w-4" />
                Request deletion (via support)
              </a>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              <strong>Retention notice.</strong> Tax invoices and accounting records are retained
              for 7 years per the Tax Administration Act regardless of deletion requests.
            </p>
          </div>
        </div>
      </Card>

      {error && <NotificationCard tone="danger" title="Could not load request history" body={error} />}

      <Card interactive={false}>
        <header className="border-b border-border p-4">
          <h3 className="text-sm font-semibold text-navy-primary">Past requests</h3>
          <p className="text-xs text-muted-foreground">
            Audit trail of every data export you've requested.
          </p>
        </header>
        {!requests ? (
          <UniversalLoadingState variant="spinner" caption="Loading…" />
        ) : requests.length === 0 ? (
          <div className="p-12 text-center">
            <FileJson className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium text-navy-primary">No exports yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Click "Download my data" above to create your first export.
            </p>
          </div>
        ) : (
          <ol className="divide-y divide-border">
            {requests.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 p-3 text-sm">
                <div>
                  <p className="font-medium text-navy-primary">{formatDateTime(r.requested_at)}</p>
                  {r.completed_at && r.completed_at !== r.requested_at && (
                    <p className="text-xs text-muted-foreground">
                      Completed {formatDateTime(r.completed_at)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge tone={r.status === 'complete' ? 'active' : 'pending'}>
                    {r.status}
                  </StatusBadge>
                  <code className="text-xs text-muted-foreground">{r.id.slice(0, 8)}</code>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}
